import { flash, Tabs } from "@xola/ui-kit";
import React, { useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Plugin } from "types";
import { useSeller } from "../hooks/useAccount";
import { NPS_SUBMENUS } from "../pages/Menu/CustomMenuItems";
import { AppIFrame, Message } from "./AppIFrame";

interface PluginCanvasProps {
    plugin?: Plugin;
    height: number;
}

const fragmentRouteMap: Record<string, string> = {
    customers: "/customers/contacts",
    orders: "/purchases",
};

export const PluginCanvas = ({ plugin, height }: PluginCanvasProps) => {
    const history = useHistory();
    const { section } = useParams<{ section?: string }>();
    const seller = useSeller();

    const isNpsPlugin = plugin?.ui?.menu === "reports.NetPromoterScore";
    const activeTab = useMemo(() => {
        if (isNpsPlugin) {
            const index = NPS_SUBMENUS.findIndex((sub) => sub.path === section);
            return index === -1 ? 0 : index;
        }
        return 0;
    }, [isNpsPlugin, section]);

    const handleTabChange = useCallback(
        (tabIndex: number) => {
            if (isNpsPlugin && plugin) {
                const sub = NPS_SUBMENUS[tabIndex];
                history.push(`/pluginCanvas/${plugin.id}/${sub.path}`);
            }
        },
        [isNpsPlugin, plugin, history],
    );

    const getIFrameUrl = useCallback(() => {
        if (!plugin?.ui?.url) return "";
        const baseUrl = plugin.ui.url.replace(/\/$/, "");
        if (isNpsPlugin) {
            const npsSection = section ?? NPS_SUBMENUS[0].path;
            return `${baseUrl}/${npsSection}?seller=${seller.id}&xolaDomain=${seller.meta.xola_domain}`;
        }
        if (section) {
            return `${baseUrl}/${section}?seller=${seller.id}&xolaDomain=${seller.meta.xola_domain}`;
        }
        return `${baseUrl}?seller=${seller.id}&xolaDomain=${seller.meta.xola_domain}`;
    }, [plugin, isNpsPlugin, section, seller.id, seller.meta.xola_domain]);

    const handleMessage = useCallback(
        ({ event, data }: Message) => {
            switch (event) {
                case "success":
                    flash.success(data.message);
                    break;
                case "error":
                    flash.danger(data?.message ?? "Error installing plugin");
                    break;
                case "navigate":
                    if (!data.options || data.options?.trigger) {
                        // Options is specified with trigger === true OR there are no options
                        let fragment = fragmentRouteMap[data.type] ?? `/${data.type}`;
                        if (data.params) {
                            fragment += `/${data.params[0]}`;
                        }

                        console.log(
                            `%cNavigating to ${data.type} "${data.params[0]}" -- It may or may not have been implemented`,
                            "color:red",
                        );
                        history.push(fragment);
                    }
                    break;
                default:
                // intentionally left empty
            }
        },
        [history],
    );

    if (!plugin?.ui?.url) {
        return null;
    }

    if (isNpsPlugin) {
        return (
            <Tabs value={activeTab} onChange={handleTabChange}>
                {NPS_SUBMENUS.map((sub) => (
                    <Tabs.Tab key={sub.path}>{sub.label}</Tabs.Tab>
                ))}
                {NPS_SUBMENUS.map((sub, index) => (
                    <Tabs.Panel key={sub.path} isActive={activeTab === index}>
                        <AppIFrame url={getIFrameUrl()} height={height} onMessage={handleMessage} />
                    </Tabs.Panel>
                ))}
            </Tabs>
        );
    }

    return <AppIFrame url={getIFrameUrl()} height={height} onMessage={handleMessage} />;
};
